import { uiText } from '../i18n/uiText';
import { useSyncExternalStore } from 'react';

export function createResourceStore({ key, loader, ttl = 30000, initialState = {} }) {
  const makeInitialState = () => ({
    data: null,
    loading: false,
    error: '',
    lastLoaded: 0,
    promise: null,
    paramsKey: '',
    ...initialState,
  });
  let state = makeInitialState();
  let requestGeneration = 0;
  const listeners = new Set();

  const emit = () => listeners.forEach((listener) => listener());
  const setState = (patch) => {
    state = { ...state, ...(typeof patch === 'function' ? patch(state) : patch) };
    emit();
  };

  const getState = () => state;
  const subscribe = (listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  };

  const stableKey = (params = {}) => JSON.stringify(params || {});

  const load = async (params = {}, options = {}) => {
    const force = !!options.force;
    const nextParamsKey = stableKey(params);
    const canReuse = !force && state.data !== null && state.paramsKey === nextParamsKey && (Date.now() - state.lastLoaded < ttl);
    if (canReuse) return state.data;
    if (state.promise && state.paramsKey === nextParamsKey && !force) return state.promise;

    const generation = ++requestGeneration;
    const promise = (async () => {
      setState({ loading: true, error: '', paramsKey: nextParamsKey, promise: null });
      try {
        const data = await loader(params);
        if (generation === requestGeneration) {
          setState({ data, loading: false, error: '', lastLoaded: Date.now(), promise: null, paramsKey: nextParamsKey });
        }
        return data;
      } catch (error) {
        const message = error?.friendlyMessage || error?.message || uiText("Ошибка загрузки");
        if (generation === requestGeneration) {
          setState({ loading: false, error: message, promise: null, paramsKey: nextParamsKey });
        }
        throw error;
      }
    })();

    setState({ promise, paramsKey: nextParamsKey });
    return promise;
  };

  const invalidate = () => setState({ lastLoaded: 0, promise: null });
  const reset = () => {
    requestGeneration += 1;
    state = makeInitialState();
    emit();
  };

  const useStore = (selector = (value) => value) => useSyncExternalStore(subscribe, () => selector(state), () => selector(state));

  return { key, getState, subscribe, setState, load, invalidate, reset, useStore };
}
